import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Spinner } from '@yandex-360/orb-components/dist/components/Spinner';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { useActiveDeviceStream } from '../../../../device-manager/hooks';
import { getVideoInputError } from '../../../../device-manager/selectors';
import { deviceManager } from '../../../../goloom-sdk';
import { FacingMode, selectVirtualBackgroundType } from '../../../../settings';
import css from './styles.css';

export interface VideoPreviewProps {
    deviceIdOrFacingMode?: FacingMode | string;
}

export const VideoPreview: FunctionComponent<VideoPreviewProps> = ({ deviceIdOrFacingMode }) => {
    const videoInputError = useSelector(getVideoInputError);

    const videoRef = useRef<HTMLVideoElement>(null);

    const virtualBackgroundType = useSelector(selectVirtualBackgroundType);

    const [mediaStream, setMediaStream] = useState<Maybe<MediaStream>>();
    const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
    const activeDeviceMediaStream = useActiveDeviceStream('videoinput', true);

    useEffect(() => setIsVideoReady(false), [deviceIdOrFacingMode]);

    useLayoutEffect(() => {
        if (videoRef.current) {
            videoRef.current.srcObject = activeDeviceMediaStream ?? mediaStream ?? null;
        }
    }, [mediaStream, activeDeviceMediaStream]);

    useLayoutEffect(() => {
        if (deviceManager.videoStatus) {
            return;
        }
        const cameraStreamPromise = deviceManager.getLocalPreviewCameraStream(deviceIdOrFacingMode);

        cameraStreamPromise.then((cameraStream) => {
            setMediaStream(cameraStream ?? undefined);
        });
        return () => {
            deviceManager.stopPreviewVideoStream();
        };
    }, [deviceIdOrFacingMode, virtualBackgroundType]);

    useLayoutEffect(() => {
        const currentVideoRef = videoRef.current;
        if (currentVideoRef) {
            let timer: Maybe<ReturnType<typeof setTimeout>>;

            const onCanPlayThrough = () => {
                timer = setTimeout(() => setIsVideoReady(true), 100);
            };

            currentVideoRef.addEventListener('canplaythrough', onCanPlayThrough);

            return () => {
                currentVideoRef.removeEventListener('canplaythrough', onCanPlayThrough);
                if (timer) {
                    clearTimeout(timer);
                }
            };
        }
    }, []);

    const withMirror = deviceIdOrFacingMode !== FacingMode.ENVIRONMENT;
    const isSpinVisible = !isVideoReady && !videoInputError;

    return (
        <div className={css.background}>
            <video
                className={cn(css.video, {
                    [css.mirror]: withMirror,
                    [css.hidden]: isSpinVisible,
                })}
                ref={videoRef}
                autoPlay
                playsInline
                // remove YaBro custom buttons
                // eslint-disable-next-line react/no-unknown-property
                x-yandex-pip="false"
            />
            {isSpinVisible && <Spinner size="lg" view="orb" progress />}
        </div>
    );
};
